import { useRef } from 'react';
import { CustomSelect } from '@/components';
import {
  WINDEX_FEE_PLACEHOLDERS,
  windexNoResultNextOrder,
} from '../utils/windexStep';

const fieldClass =
  'w-full min-w-0 bg-white text-text-primary border border-border-default rounded-[8px] px-3 text-sm font-sf-pro-display outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/15 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 16 }, (_, index) => {
  const start = currentYear - index;
  const label = `${start}-${String(start + 1).slice(-2)}`;
  return { value: label, label };
});

const PLACEHOLDER_LABELS = {
  StudentName: 'Student name',
  PendingFee: 'Pending fee',
  StandardName: 'Standard',
  Class: 'Class',
  SectionName: 'Section',
  RollNo: 'Roll number',
  TotalYearlyFee: 'Yearly fee',
  TotalPaidFee: 'Paid fee',
  TotalDiscount: 'Discount',
};

function insertAtCursor(el, token, value, onChange) {
  const start = el?.selectionStart ?? value.length;
  const end = el?.selectionEnd ?? value.length;
  const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
  onChange(next);
  const caret = start + token.length;
  requestAnimationFrame(() => {
    el?.focus();
    el?.setSelectionRange(caret, caret);
  });
}

export function WindexStepFields({
  step,
  steps,
  canEdit,
  showErrors,
  onChange,
}) {
  const replyRef = useRef(null);
  const year = String(step.windex_year || '').trim();
  const yearMissing = showErrors && !year;
  const yearOptions = YEAR_OPTIONS.some((item) => item.value === year)
    ? YEAR_OPTIONS
    : year
      ? [{ value: year, label: year }, ...YEAR_OPTIONS]
      : YEAR_OPTIONS;
  const templateMissing =
    showErrors && !String(step.windex_reply_template || '').trim();
  const reply = step.windex_reply_template || '';

  return (
    <div className="space-y-5 min-w-0">
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-text-primary">
          Academic year
        </label>
        <CustomSelect
          value={year}
          disabled={!canEdit}
          onChange={(val) => onChange({ windex_year: val || '' })}
          options={yearOptions}
          placeholder="Select year"
          hideCheckIcon
          textSizeClass="text-body-sm"
          buttonClassName={`w-full min-w-0 h-10 flex items-center justify-between gap-2 rounded-[8px] bg-white border text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary px-3 ${yearMissing ? 'border-red-400 ring-2 ring-red-400' : 'border-border-default'}`}
        />
        <p className="text-xs text-text-muted">
          School year on the fee record, for example 2026-27.
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-text-primary">
          WhatsApp reply
        </label>
        <textarea
          ref={replyRef}
          rows={5}
          disabled={!canEdit}
          value={reply}
          placeholder="Hi {{StudentName}}, your pending fee is {{PendingFee}}."
          onChange={(e) => onChange({ windex_reply_template: e.target.value })}
          className={`${fieldClass} py-2.5 resize-none leading-relaxed ${templateMissing ? 'ring-2 ring-red-400' : ''}`}
        />
        <p className="text-xs text-text-muted">
          Click a detail to drop it into the message.
        </p>
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {WINDEX_FEE_PLACEHOLDERS.map((name) => (
            <button
              key={name}
              type="button"
              disabled={!canEdit}
              title={`{{${name}}}`}
              onClick={() =>
                insertAtCursor(replyRef.current, `{{${name}}}`, reply, (next) =>
                  onChange({ windex_reply_template: next }),
                )
              }
              className="rounded-full border border-border-default bg-white px-2.5 py-1 text-xs text-text-secondary hover:border-primary hover:text-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {PLACEHOLDER_LABELS[name] || name}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-text-primary">
          When no student is found
        </label>
        <input
          type="text"
          disabled={!canEdit}
          value={step.windex_no_result_message || ''}
          placeholder="No student found for this year."
          onChange={(e) =>
            onChange({ windex_no_result_message: e.target.value })
          }
          className={`${fieldClass} h-10`}
        />
        <p className="text-xs text-text-muted">
          Sent if this year has no matching student. Empty route ends the flow.
        </p>
      </div>

      <RouteSelect
        label="If student found, go to"
        value={step.next_step_order}
        steps={steps}
        currentOrder={step.order}
        blockedOrder={windexNoResultNextOrder(step)}
        disabled={!canEdit}
        onChange={(order) => onChange({ next_step_order: order })}
      />
      <RouteSelect
        label="If no student found, go to"
        value={windexNoResultNextOrder(step)}
        steps={steps}
        currentOrder={step.order}
        blockedOrder={step.next_step_order}
        disabled={!canEdit}
        onChange={(order) =>
          onChange({ windex_no_result_next_step_order: order })
        }
      />
    </div>
  );
}

function RouteSelect({
  label,
  value,
  steps,
  currentOrder,
  blockedOrder,
  disabled,
  onChange,
}) {
  const options = [
    { value: '', label: 'No next node' },
    ...(steps || [])
      .filter((item) => Number(item.order) !== Number(currentOrder))
      .filter((item) => Number(item.order) !== Number(blockedOrder))
      .map((item) => ({
        value: String(item.order),
        label: `${item.order}. ${item.question_text || 'Step'}`,
      })),
  ];
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-text-primary">
        {label}
      </label>
      <CustomSelect
        value={value == null ? '' : String(value)}
        disabled={disabled}
        onChange={(val) => onChange(val ? Number(val) : null)}
        options={options}
        hideCheckIcon
        textSizeClass="text-body-sm"
        buttonClassName="w-full min-w-0 h-10 flex items-center justify-between gap-2 rounded-[8px] bg-white border border-border-default text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary px-3"
      />
    </div>
  );
}
